import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button, ScrollToTop } from '@/components';
import { useBookingStore } from '@/store';

const BankTransferPayment: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { balance } = useBookingStore();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const bankDetails = {
    bankName: 'Bangkok Bank',
    accountName: 'Thai Hand Massage Co., Ltd.',
    accountNumber: '123-4-56789-0',
    branch: 'Central World Branch',
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleConfirmPayment = () => {
    if (!uploadedFile) {
      alert(t('bankTransfer.uploadSlip'));
      return;
    }

    // Process payment confirmation
    navigate('/booking/thank-you');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <ScrollToTop />
      <div className="mx-auto max-w-2xl px-4">
        <h2 className="mb-6 text-2xl font-bold">{t('bankTransfer.title')}</h2>

        <div className="space-y-6">
          {/* Bank Details */}
          <div className="rounded-lg bg-blue-50 p-6">
            <h3 className="mb-4 text-lg font-semibold">
              {t('bankTransfer.bankDetails')}
            </h3>
            <div className="space-y-2">
              <p>
                <span className="font-medium">
                  {t('bankTransfer.bankName')}:
                </span>{' '}
                {bankDetails.bankName}
              </p>
              <p>
                <span className="font-medium">
                  {t('bankTransfer.accountName')}:
                </span>{' '}
                {bankDetails.accountName}
              </p>
              <p>
                <span className="font-medium">
                  {t('bankTransfer.accountNumber')}:
                </span>{' '}
                {bankDetails.accountNumber}
              </p>
              <p>
                <span className="font-medium">{t('payment.branch')}:</span>{' '}
                {bankDetails.branch}
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="rounded-lg bg-yellow-50 p-4">
            <h4 className="mb-2 font-semibold text-yellow-800">
              {t('bankTransfer.instructions')}
            </h4>
            <ol className="list-inside list-decimal space-y-1 text-sm text-yellow-700">
              <li>{t('bankTransfer.step1')}</li>
              <li>{t('bankTransfer.step2')}</li>
              <li>{t('bankTransfer.step3')}</li>
              <li>{t('bankTransfer.step4')}</li>
            </ol>
          </div>

          {/* Amount */}
          <div className="rounded-lg bg-green-50 p-4 text-center">
            <p className="text-sm text-gray-600">
              {t('promptpay.amountToPay')}
            </p>
            <p className="text-2xl font-bold text-green-600">
              {t('common.currency')}
              {balance.total.toLocaleString()}
            </p>
          </div>

          {/* Upload Payment Slip */}
          <div className="rounded-lg border bg-white p-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t('bankTransfer.uploadSlip')}
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            {uploadedFile && (
              <p className="mt-2 text-sm text-green-600">
                ✓ {uploadedFile.name} uploaded successfully
              </p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {t('bankTransfer.uploadInstructions')}
            </p>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            <Button
              variant="secondary"
              onClick={() => navigate('/booking/summary')}
            >
              {t('common.back')}
            </Button>
            <Button
              onClick={handleConfirmPayment}
              disabled={!uploadedFile}
              className={!uploadedFile ? 'cursor-not-allowed opacity-50' : ''}
            >
              {t('bankTransfer.confirmPayment')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankTransferPayment;
